import { useContext, useState } from 'react';
import { Dialog } from 'components/Atoms/Dialog/Dialog';
import { Flex } from 'components/Atoms/Flex/Flex';
import { Icon } from 'components/Atoms/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { Spacer } from 'components/Atoms/Spacer/Spacer';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { PageContext } from 'src/context/PageContext';
import classNames from 'classnames';
import validator from 'validator';
import { subscribeToNotifications } from 'src/service/engageFrontendGateway';
import styles from './NotificationSignup.module.scss';

interface NotificationSignupDialogProps {
  popupOpen: boolean;
  setPopupOpen: (open: boolean) => void;
  residentCustomPublicationIds?: string[];
}
export function NotificationSignupDialog(props: NotificationSignupDialogProps) {
  const { page, urlParams, survey, project } = useContext(PageContext);
  const { locale, organization } = urlParams || {};
  const { t } = useTranslation();
  const { popupOpen, setPopupOpen, residentCustomPublicationIds } = props;
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);

  const projectTitle =
    typeof project?.title === 'string'
      ? project?.title
      : project?.title?.[urlParams?.locale || ''] || project?.title?.[Object.keys(project?.title || {})[0]];
  const titleLocale = urlParams?.locale || survey?.default_language || survey?.fallback_language || '';
  const surveyTitle =
    survey?.title_for_display?.[titleLocale] ||
    survey?.title_for_display?.[survey.default_language!] ||
    survey?.title_for_display?.[survey.fallback_language!];
  const surveyProjectObject =
    !!survey && !!project
      ? {
          id: survey.structure.id,
          page_type: page?.page_type ?? 'activity',
          customer_id: survey.client?.id,
          name: surveyTitle || projectTitle || project?.slug,
        }
      : null;

  const validEmail = validator.isEmail(email);

  const isFormValid = () => validEmail;

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setShowError(true);
      return;
    }
    setShowError(false);

    // Preview mode collects no data: close the dialog without any outgoing call.
    if (urlParams?.preview) {
      setPopupOpen(false);
      return;
    }

    if (page && organization && locale) {
      await subscribeToNotifications(
        email,
        page,
        organization as string,
        locale as string,
        residentCustomPublicationIds,
      );
    } else if (organization && locale && survey) {
      if (surveyProjectObject) {
        await subscribeToNotifications(
          email,
          surveyProjectObject as never,
          organization as string,
          locale as string,
          residentCustomPublicationIds,
        );
      } else {
        await subscribeToNotifications(
          email,
          {
            customer_id: survey.client?.id,
            page_type: 'activity',
            id: survey.structure.id,
          } as never,
          organization as string,
          locale as string,
          residentCustomPublicationIds,
        );
      }
    }
    setPopupOpen(false);
  };

  return (
    <Dialog
      open={popupOpen}
      description={
        <div>
          <div className={styles.head}>
            <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
              <div className={styles.heading}>{t('notificationSignup.title')}</div>
              <button type="button" aria-label={t('icons.close')} onClick={() => setPopupOpen(false)}>
                <Icon name="close" />
              </button>
            </Flex>
          </div>
          <div className={styles.content}>
            {t('notificationSignup.email')}:{' '}
            <input
              type="email"
              className={classNames(styles.input, {
                [styles.inputError]: showError && !validEmail,
              })}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-label={t('notificationSignup.email')}
            />
            {showError && !validEmail && (
              <div className={styles.errorMessage}>
                <Icon name="warning" /> {t('notificationSignup.requiredEmail')}
              </div>
            )}
            <Spacer size="small" orientation="vertical" />
            <div
              className={styles.privacyText}
              dangerouslySetInnerHTML={{ __html: t('notificationSignup.privacyDisclaimer') }}
            />
            <Spacer size="small" orientation="vertical" />
            <ActionButton type={ButtonType.ACTION} onClick={handleSubmit}>
              {t('notificationSignup.subscribe')}
            </ActionButton>
          </div>
        </div>
      }
      noPadding
    />
  );
}
